import { ChevronRight, Close } from '@mui/icons-material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, Grid, IconButton, Link, Paper, Stack, styled } from '@mui/material';
import { useState, useEffect } from 'react';
import { BsBookmark, BsFillBookmarkFill } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import {
  getJobById,
  IJob,
  IWorkAssignmentDTO,
  getWorkAssignmentByCurrentRecruiterAndJob,
  pinJob,
  removePinJob,
  getEvaluationTemplateByJobId,
  getInfoTemplateByJobId,
  IPosition,
  getJobRevenue,
} from 'wisestep-api';
import {
  ScrollableTabs,
  TabTextVariant,
  WsButton,
  WsColors,
  WsIconActiveJob,
  WsShareJob,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { useWorkspaceContext } from '../../../../../context';
import { ClintPageBreadCrumb } from '../../../../../features/clientDetails/helper/clientDetailsHelper';
import { JobBoardsAction, useJobAccessControl } from '../../../../../hooks/useJobAccessControl';
import { careerPageSettingStore } from '../../../../../utils/careerPageSettingsStore';
import getEncodedQueryParams from '../../../../../utils/getEncodedQueryParams';
import { success } from '../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../../../utils/openUrlInNewTab';
import socialMediaShareUrl, { indexJob } from '../../../../../utils/shareUrl';
import { getRecruiterAnalyticsUrl } from '../../../../candidates/utils/CandidateHelper';
import { downloadJD } from '../../hooks';
import { copyStringToClipboard } from '../jobCardRightChild/JobCardRightChild';

import Assignees from './assignees';
import ClientDetails from './clientDetails';
import JobInfo from './jobInfo';
import PotentialRevenue from './potentialRevenue';
import Published from './published';

interface viewJDProps {
  closeDialog: () => void;
  job: IJob;
  defaultTab?: number;
  openJobAdminTeam?: boolean;
  openWorkAssignmentTeam?: boolean;
}

const HeaderTheme = styled(Box)(() => ({
  padding: 1,
  backgroundColor: WsColors.TertiaryShades50,
  borderRadius: 4,
}));
const ViewJDLayout = ({
  closeDialog,
  job,
  defaultTab = 0,
  openJobAdminTeam = false,
  openWorkAssignmentTeam = false,
}: viewJDProps) => {
  const [value, setValue] = useState(defaultTab);
  const navigate = useNavigate();
  const [jobEntity, setJobEntity] = useState<IJob>();
  const [refresh, setRefresh] = useState<boolean>(false);
  const [refreshWorkAssignment, setRefreshWorkAssignment] = useState<boolean>(false);
  const [workAssignment, setWorkAssignment] = useState<IWorkAssignmentDTO>();
  const [mappedEvalutaions, setMappedEvaluations] = useState();
  const [mappedInfoTemplates, setMappedInfoTemplates] = useState();
  const [revenue, setRevenue] = useState<IPosition>();
  const workspace = useWorkspaceContext();

  const { access: jobAccesControl } = useJobAccessControl([
    JobBoardsAction.POTENTIAL_REVENUE_VISIBILITY,
  ]);
  const potentialRevenueVisible = (workspace.owns || jobAccesControl[0]) ?? false;

  const refreshJob = () => setRefresh(!refresh);

  const refreshWorkAssignmentTeam = () => setRefreshWorkAssignment(!refreshWorkAssignment);

  useEffect(() => {
    const getWorkAssignment = async () => {
      if (job.id) {
        const jobDetailsResponse = await getJobById(job.id);
        setJobEntity(jobDetailsResponse.data);
      }
    };
    getWorkAssignment();
  }, [job.id, refresh]);

  useEffect(() => {
    const getWorkAssignment = async () => {
      if (job.id) {
        const userTeamsResponse = await getWorkAssignmentByCurrentRecruiterAndJob(job.id);
        setWorkAssignment(userTeamsResponse.data);
      }
    };

    const getEvaluationMappings = async () => {
      if (job.id) {
        const evaluationReponse = await getEvaluationTemplateByJobId(job.id);
        setMappedEvaluations(evaluationReponse.data);
      }
    };

    const getInformationTemplates = async () => {
      if (job.id) {
        const res = await getInfoTemplateByJobId(job.id);
        setMappedInfoTemplates(res.data);
      }
    };

    getWorkAssignment();
    getEvaluationMappings();
    getInformationTemplates();
  }, [job, refreshWorkAssignment]);

  const tabsList = [
    {
      tabName: 'Job info',
    },
    {
      tabName: 'Client Details',
    },
    {
      tabName: 'Assignees',
    },
    {
      tabName: 'Published on',
    },
    {
      tabName: 'Potential Revenue',
    },
  ];

  const careerPageSetting = careerPageSettingStore((state) => state.careerPageSettings);
  const indexJobToCareerPage = async () => indexJob(job, careerPageSetting);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const pinnedJob = async () => {
    if (job.id) {
      const response = await pinJob(job.id);
      if (response.data) {
        setNotification(success('The job is added to your favorites list.'));
      }
    }
  };

  const unPinnedJob = async () => {
    if (job.id) {
      const response = await removePinJob(job.id);
      if (response.data) {
        setNotification(success('The job is removed from your favorites list.'));
      }
    }
  };
  const openTalentPool = () => openUrlInNewTab(`/candidates/${job.id}`);

  const handleClipBoard = (data: string) => {
    copyStringToClipboard(data);
    setNotification(success('Copied Successfully !!'));
  };

  useEffect(() => {
    const fetchData = async () => {
      const response = await getJobRevenue(jobEntity?.id ?? '');
      const updatedResponse = {
        ...response.data,
        jobDuration: jobEntity?.workDuration,
        jobDurationTimeSpan: jobEntity?.workDurationTimeSpan,
      };
      setRevenue(updatedResponse);
    };
    fetchData();
  }, [jobEntity?.id, jobEntity?.workDuration, jobEntity?.workDurationTimeSpan]);
  return (
    <Box>
      <HeaderTheme>
        <Stack direction="row">
          <Grid item xs={6} container pt={1.5}>
            <Box pt={0.3} px={0.5}>
              <WsIconActiveJob />
            </Box>
            {job.title && (
              <Box pt={0.2}>
                <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                  {job.title}
                </WsTypography>
              </Box>
            )}
            &nbsp;
            {job.jobCode && (
              <Box pt={0.5} px={0.5}>
                <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY}>
                  {job.jobCode}
                </WsTypography>
              </Box>
            )}
            {job?.companyName && !/.*xxxxxx.*/.test(job.companyName) ? (
              job.companyName && (
                <Box pt={0.2}>
                  <Link
                    href={`/client-details?tabIndex=0&isChild=true&name=${
                      job?.companyName
                    }&clientId=${job?.clientId}&breadCrumbList=${getEncodedQueryParams(
                      ClintPageBreadCrumb['CLIENTS']
                    )}`}
                    underline="none"
                  >
                    <WsTypography
                      variant={WsTypoVariants.SUB_TITLE}
                      color={WsTypoColors.PRIMARY}
                      cursor="pointer"
                    >
                      {` @ ${job.companyName}`}
                    </WsTypography>
                  </Link>
                </Box>
              )
            ) : (
              <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY}>
                {` @ ${job.companyName}`}
              </WsTypography>
            )}
          </Grid>
          <Grid item xs={6} container justifyContent="flex-end">
            <Box textAlign="right" sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1}>
                <Box mt={1.5} display={'flex'}>
                  <WsTypography
                    variant={WsTypoVariants.BODY2}
                    color={WsTypoColors.BLACK}
                    cursor="pointer"
                  >
                    Last update by
                  </WsTypography>
                  <Box sx={{ mr: 0.5 }}></Box>
                  <WsTypography
                    variant={WsTypoVariants.BODY2}
                    color={WsTypoColors.PRIMARY600}
                    cursor="pointer"
                    onClick={async () => {
                      const url =
                        job?.modifiedBy && (await getRecruiterAnalyticsUrl(job?.modifiedBy));
                      url && navigate(url);
                    }}
                  >
                    {job?.modifiedBy ?? ''}
                  </WsTypography>
                </Box>
                <Box mx={1}>
                  <IconButton
                    onClick={() => downloadJD(job.id ?? '')}
                    sx={{ p: 0, mt: 1, pr: 0.5 }}
                  >
                    <FileDownloadOutlinedIcon
                      sx={{ color: WsColors.TertiaryShades500, width: 25 }}
                    />
                  </IconButton>
                </Box>
                <Box mx={1}>
                  <IconButton
                    onClick={job.isJobPinned ? unPinnedJob : pinnedJob}
                    sx={{ p: 0, mt: 1.4 }}
                  >
                    {job.isJobPinned ? <BsFillBookmarkFill size={17} /> : <BsBookmark size={17} />}
                  </IconButton>
                </Box>

                <Box mx={1} pt={5} p={1} mt={1}>
                  <WsShareJob
                    indexJob={indexJobToCareerPage}
                    shareUrl={socialMediaShareUrl(job, careerPageSetting)}
                    shareUrlLinkedIn=""
                    title={job.title ?? ''}
                    isGrey={true}
                    iconSize="small"
                    clipBoard={handleClipBoard}
                  />
                </Box>
              </Stack>
            </Box>
            <Box mx={1}>
              <IconButton onClick={closeDialog} sx={{ p: 0, mt: 2 }}>
                <Close sx={{ color: WsColors.TertiaryShades500 }} />
              </IconButton>
            </Box>
          </Grid>
        </Stack>
      </HeaderTheme>
      <Box>
        <ScrollableTabs
          scrollList={tabsList}
          getTabIndex={(index: number) => {
            setValue(index);
          }}
          variant={TabTextVariant.SUB_TITLE}
          defaultValue={value}
        />
      </Box>
      <Box m={2}>
        {value == 0 && (
          <JobInfo
            job={job}
            mappedEvalutaions={mappedEvalutaions}
            mappedInfoTemplates={mappedInfoTemplates}
          />
        )}
        {value == 1 && <ClientDetails job={job} />}
        {value == 2 && jobEntity && (
          <Assignees
            job={jobEntity}
            workAssignment={workAssignment}
            refreshJob={refreshJob}
            refreshWorkAssignmentTeam={refreshWorkAssignmentTeam}
            openJobAdminTeam={openJobAdminTeam}
            openWorkAssignmentTeam={openWorkAssignmentTeam}
          />
        )}
        {value == 3 && jobEntity && <Published job={jobEntity} />}
        {value == 4 && jobEntity && potentialRevenueVisible && (
          <PotentialRevenue jobRevenueInfo={revenue} />
        )}
      </Box>
      <Paper sx={{ p: 1 }}>
        <Grid item xs={12} container justifyContent="flex-end">
          <WsButton
            variant="contained"
            size="large"
            endIcon={<ChevronRight />}
            onClick={openTalentPool}
          >
            Start Sourcing
          </WsButton>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ViewJDLayout;
